// Voice editor — header + 7 sections (US-091 → US-103, US-101 + US-102).
//
// Composes the entire per-account voice surface from
// `prototypes_and_docs/ls-voice.jsx:813-1242`. We keep the prototype's
// auto-save semantics (each control mutates immediately via
// `useUpdateVoice`). The explicit action therefore says "Done" and returns
// to notification settings; it never claims to batch-save or revert changes.
// The history-analysis flow is wired via `useAnalyzeHistory` +
// `useApplySuggestions`, with a progress toast + diff modal between them.

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import {
  ApiError,
  useAnalyzeHistory,
  useApplySuggestions,
  useNotifyTest,
  useUpdateLinkedinAccount,
  useUpdateVoice,
  useUserHubChannel,
  useVoice,
  type VoicePatch,
  type VoiceRow,
  type VoiceSuggestionPatch,
} from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Banner, Button, PageHeader, Skeleton, toast } from '@barel/ui';

import { runMutationWithToast } from '@/lib/mutationToast';
import { LeadTempInfo } from '@/features/relances/LeadTempInfo';
import { RelancesSection } from '@/features/relances/RelancesSection';

import { AnalyseHistoryBanner } from './AnalyseHistoryBanner';
import { AnalysisProgressToast } from './AnalysisProgressToast';
import { AnalysisSuggestionsModal } from './AnalysisSuggestionsModal';
import { IdentitySection } from './sections/IdentitySection';
import { ModeSection } from './sections/ModeSection';
import { NotificationsSection } from './sections/NotificationsSection';
import { PhrasesSection } from './sections/PhrasesSection';
import { SectionShell } from './sections/SectionShell';
import { SmartLimitsSection } from './sections/SmartLimitsSection';
import { ToneSection } from './sections/ToneSection';

export interface VoiceEditorPageProps {
  linkedinAccountId: string;
}

export function VoiceEditorPage({ linkedinAccountId }: VoiceEditorPageProps) {
  const t = useT('voice');
  const tBilling = useT('billing');
  const navigate = useNavigate();
  const query = useVoice(linkedinAccountId);
  const update = useUpdateVoice(linkedinAccountId);
  const updateAccount = useUpdateLinkedinAccount();
  const analyse = useAnalyzeHistory(linkedinAccountId);
  const applySuggestions = useApplySuggestions(linkedinAccountId);
  const notifyTest = useNotifyTest(linkedinAccountId);

  const [analysing, setAnalysing] = useState(false);
  const [reviewSuggestion, setReviewSuggestion] = useState<VoiceSuggestionPatch | null>(null);
  const [pendingWorkflowId, setPendingWorkflowId] = useState<string | null>(null);
  const qc = useQueryClient();
  const billingBlocked =
    query.error instanceof ApiError && query.error.code === 'billing.access_blocked';

  // Translate any unhandled query error into a toast on first render only.
  useEffect(() => {
    if (query.error && !billingBlocked) {
      toast.error({ title: t('loading.errorTitle'), description: t('loading.errorBody') });
    }
  }, [billingBlocked, query.error, t]);

  // Wave A2 — when the workflow finishes, the server emits
  // `voice.analysis-ready` with `{ workflowId, accountId }`. We invalidate
  // the `useVoice` query so the modal opens with the fresh `lastSuggestion`.
  useUserHubChannel('voice.analysis-ready', (event) => {
    const payload = (('payload' in event ? event.payload : null) ?? {}) as {
      workflowId?: string;
      accountId?: string;
    };
    if (payload.accountId !== linkedinAccountId) return;
    if (pendingWorkflowId && payload.workflowId !== pendingWorkflowId) return;
    void qc
      .invalidateQueries({ queryKey: ['voice', linkedinAccountId] })
      .then(() => qc.refetchQueries({ queryKey: ['voice', linkedinAccountId] }))
      .then(() => {
        const fresh = qc.getQueryData<{ voice: VoiceRow }>(['voice', linkedinAccountId]);
        const suggestion = fresh?.voice.lastSuggestion as
          | Record<string, unknown>
          | null
          | undefined;
        if (suggestion && Object.keys(suggestion).length > 0) {
          setReviewSuggestion(suggestion as unknown as VoiceSuggestionPatch);
        }
        setAnalysing(false);
        setPendingWorkflowId(null);
      });
  });

  if (query.isLoading) {
    return <VoiceEditorSkeleton />;
  }
  if (billingBlocked) {
    return (
      <div data-testid="voice-billing-blocked" className="flex flex-col gap-4 p-6">
        <Banner tone="warning">
          <div className="font-semibold">{tBilling('expired.softTitle')}</div>
          <p className="text-xs">{tBilling('expired.softBody')}</p>
          <Button
            type="button"
            variant="primary"
            size="sm"
            className="mt-3"
            onClick={() => {
              void navigate({ to: '/settings/$section', params: { section: 'billing' } });
            }}
            data-testid="voice-billing-recover"
          >
            {tBilling('expired.softCta')}
          </Button>
        </Banner>
      </div>
    );
  }
  if (query.error || !query.data) {
    return (
      <div data-testid="voice-editor-error" className="flex flex-col gap-4 p-6">
        <Banner tone="error">
          <div className="font-semibold">{t('loading.errorTitle')}</div>
          <p className="text-xs">{t('loading.errorBody')}</p>
        </Banner>
      </div>
    );
  }

  const { voice, account, notificationChannels } = query.data;

  const flush = (patch: VoicePatch): Promise<void> =>
    runMutationWithToast(() => update.mutateAsync(patch), {
      error: { title: t('page.saveError') },
    });

  const handleDone = (): void => {
    void navigate({ to: '/settings/$section', params: { section: 'notifications' } });
  };

  const handleAnalyse = async (): Promise<void> => {
    setAnalysing(true);
    await runMutationWithToast(() => analyse.mutateAsync(), {
      // Wave A2 — the actual suggestion arrives via the
      // `voice.analysis-ready` WS listener above; it invalidates
      // `useVoice`, reads `voice.lastSuggestion`, and pops the modal.
      // Until the event fires we keep the progress toast active.
      onSuccess: (ack) => {
        const ackWorkflowId =
          (ack as { workflowId?: string } | null | undefined)?.workflowId ?? null;
        setPendingWorkflowId(ackWorkflowId);
      },
      error: {
        title: t('history.errorTitle'),
        description: t('history.errorBody'),
      },
      onError: () => setAnalysing(false),
    });
  };

  const handleApply = (patch: VoiceSuggestionPatch): Promise<void> =>
    runMutationWithToast(() => applySuggestions.mutateAsync({ patch }), {
      success: (result) => ({
        title: t('history.successTitle', { count: result.applied }),
      }),
      onSuccess: () => setReviewSuggestion(null),
      error: { title: t('page.saveError') },
    });

  return (
    <div data-component="VoiceEditorPage" className="flex min-h-full flex-col bg-bg-base">
      <PageHeader
        title={t('page.title', { accountName: account.name })}
        subtitle={t('page.subtitle')}
        actions={
          <Button
            variant="primary"
            size="md"
            onClick={handleDone}
            disabled={update.isPending}
            type="button"
            data-testid="voice-done-header"
          >
            {t('page.done')}
          </Button>
        }
        sticky={false}
      />

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-[720px] flex-col gap-7 px-8 py-6">
          {account.historyEligible ? (
            <AnalyseHistoryBanner
              historyConvs={account.historyConvs ?? 0}
              busy={analyse.isPending || analysing}
              onAnalyse={() => {
                void handleAnalyse();
              }}
            />
          ) : null}

          <SectionShell title={t('leadTemp.title')} subtitle={t('leadTemp.subtitle')}>
            <LeadTempInfo />
          </SectionShell>

          <IdentitySection voice={voice} flush={flush} />
          <ToneSection voice={voice} flush={flush} />
          <PhrasesSection voice={voice} flush={flush} />
          <ModeSection
            account={account}
            onPatch={(settings) =>
              runMutationWithToast(
                () => updateAccount.mutateAsync({ id: linkedinAccountId, settings }),
                { error: { title: t('page.saveError') } },
              )
            }
          />
          <SmartLimitsSection voice={voice} flush={flush} />
          <NotificationsSection
            voice={voice}
            flush={flush}
            account={account}
            notificationChannels={notificationChannels}
            onSendTest={(destination) => {
              const destinationPatch: VoicePatch =
                voice.notifyChannel === 'whatsapp'
                  ? { notifyWhatsapp: destination }
                  : voice.notifyChannel === 'telegram'
                    ? { notifyTelegram: destination }
                    : voice.notifyChannel === 'slack'
                      ? { notifySlack: destination }
                      : { notifyEmail: destination };
              void runMutationWithToast(() => update.mutateAsync(destinationPatch), {
                error: { title: t('page.saveError') },
                onSuccess: () =>
                  runMutationWithToast(() => notifyTest.mutateAsync(), {
                    success: { title: t('notifications.testToast') },
                    error: { title: t('notifications.testError') },
                  }),
              });
            }}
            testBusy={notifyTest.isPending || update.isPending}
          />

          <SectionShell title={t('relances.title')} subtitle={t('relances.subtitle')}>
            <RelancesSection linkedinAccountId={linkedinAccountId} />
          </SectionShell>

          <div className="flex justify-end pb-7 pt-2">
            <Button
              variant="primary"
              type="button"
              onClick={handleDone}
              disabled={update.isPending}
              data-testid="voice-done-bottom"
            >
              {t('page.done')}
            </Button>
          </div>
        </div>
      </div>

      <AnalysisProgressToast active={analyse.isPending || analysing} />

      {reviewSuggestion ? (
        <AnalysisSuggestionsModal
          open={true}
          voice={voice}
          suggestion={reviewSuggestion}
          onApply={handleApply}
          onClose={() => setReviewSuggestion(null)}
          busy={applySuggestions.isPending}
        />
      ) : null}
    </div>
  );
}

// ─── Skeleton ────────────────────────────────────────────────────────────────

function VoiceEditorSkeleton() {
  return (
    <div className="flex min-h-full flex-col bg-bg-base" data-testid="voice-editor-skeleton">
      <div className="border-b border-default bg-bg-surface px-6 py-4">
        <Skeleton className="h-6 w-64 rounded" />
        <Skeleton className="mt-2 h-3 w-80 rounded" />
      </div>
      <div className="mx-auto flex w-full max-w-[720px] flex-col gap-6 px-8 py-6">
        <Skeleton className="h-20 w-full rounded-xl" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
    </div>
  );
}

// Used as a placeholder for the locale type imports — keep the export so
// `@barel/api-client/react` types are reachable from the routing layer.
export type { VoiceAccountSummary, VoiceRow } from '@barel/api-client/react';
